import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from './support-logger';
import type { SupportLogServiceName } from './support-logging.types';

interface Props {
  children: ReactNode;
  serviceName?: SupportLogServiceName;
  source?: string;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class SupportErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger?.logError({
      eventName: 'fe.react_error_boundary',
      severity: 'fatal',
      serviceName: this.props.serviceName ?? 'periverse_app',
      description: error.message,
      error,
      source: this.props.source,
      extra: { componentStack: info.componentStack ?? '' },
    });
    // Prevent window.onerror from double-logging the same error
    (error as Error & { __logged?: boolean }).__logged = true;
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <div>Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}
